'use client';

import { ArrowRight, FileText, Mic, Music2, RefreshCw, Square, Trash2, Upload } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useToast } from '@/app/components/ui/use-toast';
import { useAudioUpload } from '@/app/hooks';
import type { AudioUploadFile, FormAudioPrompt } from '@/app/types';
import { cn, countLipSyncTextWeightedLength, trimLipSyncTextToMaxWeightedLength } from '@/app/utils';
import { type StudioSpeechMode, useStudioRecorder } from '../_hooks';
import {
  OFFLINE_AVATAR_MAX_AUDIO_DURATION_SECONDS,
  OFFLINE_AVATAR_MAX_AUDIO_FILE_SIZE,
  OFFLINE_AVATAR_MAX_TEXT_WEIGHTED_LENGTH,
  OFFLINE_AVATAR_MIN_AUDIO_DURATION_SECONDS,
} from '../constants';
import { StudioAudioPlayer } from './studio-audio-player';
import { StudioTabs } from './studio-tabs';
import { StudioVoiceSelector } from './studio-voice-selector';

interface SpeechComposerProps {
  mode: StudioSpeechMode;
  onModeChange: (mode: StudioSpeechMode) => void;
  script: string;
  onScriptChange: (text: string) => void;
  voice: string;
  onVoiceChange: (voice: string) => void;
  audioPrompt: FormAudioPrompt | null;
  recordedPrompt: FormAudioPrompt | null;
  onAudioUpload: (file: AudioUploadFile) => void;
  onRemoveAudio: () => void;
  onRecording: (blob: Blob, duration: number, name: string) => void;
  onRemoveRecording: () => void;
  onBusyChange: (busy: boolean) => void;
  /** 输入框（或上传/录制区）的高度，左侧角色照片跟它等高 */
  onBoxHeightChange?: (height: number) => void;
  /** 进入下一步；没填内容时 nextDisabled 置灰 */
  onNext: () => void;
  nextDisabled: boolean;
  nextLoading?: boolean;
  nextValidationMessage?: ReactNode;
  locked?: boolean;
  onLocked?: () => void;
}

const MODES = [
  { mode: 'text', Icon: FileText },
  { mode: 'audio', Icon: Music2 },
  { mode: 'record', Icon: Mic },
] as const;

export function SpeechComposer({
  mode,
  onModeChange,
  script,
  onScriptChange,
  voice,
  onVoiceChange,
  audioPrompt,
  recordedPrompt,
  onAudioUpload,
  onRemoveAudio,
  onRecording,
  onRemoveRecording,
  onBusyChange,
  onBoxHeightChange,
  onNext,
  nextDisabled,
  nextLoading = false,
  nextValidationMessage,
  locked = false,
  onLocked,
}: SpeechComposerProps) {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  const { toast } = useToast();
  const inputId = useId();
  const { selectAudio, uploading } = useAudioUpload({
    allowTempMode: true,
    minDuration: OFFLINE_AVATAR_MIN_AUDIO_DURATION_SECONDS,
    maxDuration: OFFLINE_AVATAR_MAX_AUDIO_DURATION_SECONDS,
    maxSize: OFFLINE_AVATAR_MAX_AUDIO_FILE_SIZE,
    onChange: onAudioUpload,
  });
  const finishRecording = useCallback(
    (blob: Blob, duration: number) => onRecording(blob, duration, t('speech.recordName')),
    [onRecording, t],
  );
  const recorder = useStudioRecorder(finishRecording);
  const textWeightedLength = countLipSyncTextWeightedLength(script);
  const [nextValidationOpen, setNextValidationOpen] = useState(false);
  useEffect(() => {
    if (!nextValidationMessage) setNextValidationOpen(false);
  }, [nextValidationMessage]);
  useEffect(() => {
    onBusyChange(recorder.recording || recorder.starting);
    return () => onBusyChange(false);
  }, [onBusyChange, recorder.recording, recorder.starting]);
  useEffect(() => {
    if (recorder.error === 'too-short') {
      toast({ type: 'warning', message: t('speech.recordLimit') });
    }
  }, [recorder.error, t, toast]);
  const boxRef = useRef<HTMLDivElement>(null);
  // 三种模式共用同一个弹性容器，角色高度不随各模式内容量变化。
  useEffect(() => {
    const box = boxRef.current;
    if (!box || !onBoxHeightChange || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => onBoxHeightChange(Math.round(box.getBoundingClientRect().height)));
    observer.observe(box);
    return () => observer.disconnect();
  }, [onBoxHeightChange]);
  const currentAudio = mode === 'record' ? recordedPrompt : audioPrompt;
  const source = currentAudio?.prompt?.preview || currentAudio?.prompt?.src;
  const duration = currentAudio?.prompt?.duration;
  const isUploading =
    mode === 'audio' ? uploading || audioPrompt?.status === 'uploading' : recordedPrompt?.status === 'uploading';
  const isEmptyAudioUpload = mode === 'audio' && !source && !isUploading;
  const isEmptyRecording =
    mode === 'record' && !source && !isUploading && !recorder.recording && !recorder.starting && recorder.supported;
  const isEmptyMediaAction = isEmptyAudioUpload || isEmptyRecording;
  const handleEmptyMediaAction = isEmptyAudioUpload ? selectAudio : recorder.start;
  const handleScriptChange = (nextScript: string) => {
    if (locked) {
      onLocked?.();
      return;
    }

    const trimmedScript = trimLipSyncTextToMaxWeightedLength(nextScript, OFFLINE_AVATAR_MAX_TEXT_WEIGHTED_LENGTH);
    if (trimmedScript !== nextScript) {
      toast({
        type: 'warning',
        message: t('speech.textTooLong', { count: OFFLINE_AVATAR_MAX_TEXT_WEIGHTED_LENGTH }),
      });
    }
    onScriptChange(trimmedScript);
  };

  const nextButton = (
    <CustomTooltip
      content={nextValidationMessage}
      open={Boolean(nextValidationMessage) && nextValidationOpen}
      onOpenChange={setNextValidationOpen}
      trigger="click"
      side="top"
      sideOffset={8}
    >
      <button
        type="button"
        onClick={() => {
          if (nextValidationMessage) {
            setNextValidationOpen(true);
            return;
          }
          onNext();
        }}
        disabled={nextDisabled}
        className="flex h-10 items-center justify-center gap-2 rounded-full bg-system-white px-5 font-semibold text-sm text-system-black transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-4 disabled:cursor-not-allowed disabled:opacity-30 motion-reduce:transition-none sm:w-full"
      >
        {nextLoading ? <Loading className="size-4 animate-spin" aria-hidden="true" /> : null}
        {t('next')}
        <ArrowRight className="size-4" aria-hidden="true" />
      </button>
    </CustomTooltip>
  );

  return (
    // 撑满右栏剩余高度：框吃掉 tab 下方的全部空间，屏幕矮时退回内容高度并在外层滚动
    <section aria-label={t('steps.speech.title')} className="flex min-h-0 flex-1 flex-col">
      <div
        data-speech-scroll
        className="flex min-h-0 flex-1 flex-col sm:overflow-y-auto sm:[scrollbar-width:none] sm:[&::-webkit-scrollbar]:hidden"
      >
        <StudioTabs
          value={mode}
          onChange={(value) => {
            if (locked) {
              onLocked?.();
              return;
            }
            recorder.stop();
            onModeChange(value);
          }}
          options={MODES.map(({ mode: value, Icon }) => ({
            value,
            label: t(`speech.${value}`),
            icon: <Icon className="size-4 shrink-0" aria-hidden="true" />,
          }))}
          className="mb-5 shrink-0 sm:mb-3"
        />
        <div ref={boxRef} data-speech-box-frame className="flex min-h-0 flex-1 sm:min-h-[310px] sm:flex-none">
          {mode === 'text' ? (
            <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-16 border border-transparent bg-white/[0.08] p-5 transition-colors focus-within:border-system-blue01 sm:overflow-visible sm:p-4">
              <textarea
                id={inputId}
                value={script}
                onChange={(event) => handleScriptChange(event.target.value)}
                aria-label={t('speech.label')}
                placeholder={`${t('speech.label')}${t('speech.placeholder')}`}
                className="min-h-[64px] w-full flex-1 resize-none bg-transparent text-base text-system-white leading-7 outline-none placeholder:text-system-white48 sm:min-h-[100px]"
              />
              <div className="mt-3 flex justify-end">
                <span className="text-system-white64 text-xs tabular-nums" aria-live="off">
                  {t('speech.characters', {
                    count: textWeightedLength,
                    max: OFFLINE_AVATAR_MAX_TEXT_WEIGHTED_LENGTH,
                  })}
                </span>
              </div>
              <div className="mt-4 border-system-white08 border-t pt-4">
                <StudioVoiceSelector voice={voice} onVoiceChange={onVoiceChange} locked={locked} onLocked={onLocked} />
              </div>
            </div>
          ) : (
            <div
              role={isEmptyMediaAction ? 'button' : undefined}
              tabIndex={isEmptyMediaAction ? 0 : undefined}
              aria-label={
                isEmptyAudioUpload ? t('speech.audio') : isEmptyRecording ? t('speech.recordStart') : undefined
              }
              onClick={isEmptyMediaAction ? handleEmptyMediaAction : undefined}
              onKeyDown={
                isEmptyMediaAction
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleEmptyMediaAction();
                      }
                    }
                  : undefined
              }
              className={cn(
                'flex min-h-0 w-full flex-1 flex-col items-center justify-center overflow-hidden rounded-[20px] border border-system-white16 border-dashed bg-system-white02 p-8 text-center sm:overflow-visible sm:p-5',
                isEmptyMediaAction &&
                  'cursor-pointer transition-colors hover:border-system-white32 hover:bg-system-white04 focus-visible:border-system-blue01 focus-visible:outline-none motion-reduce:transition-none',
              )}
            >
              {isUploading ? (
                <output className="flex flex-col items-center text-system-white80">
                  <Loading className="mb-4 size-8 animate-spin" aria-hidden="true" />
                  <p className="font-medium text-sm">{t('speech.audioUploading')}</p>
                </output>
              ) : source && !isUploading ? null : (
                <span
                  className={cn(
                    'mb-5 flex size-16 items-center justify-center rounded-full',
                    recorder.recording && mode === 'record'
                      ? 'bg-red-500/15 text-red-400'
                      : 'bg-system-white08 text-system-white80',
                  )}
                >
                  {mode === 'audio' ? (
                    <Upload className="size-6" aria-hidden="true" />
                  ) : (
                    <Mic className="size-6" aria-hidden="true" />
                  )}
                </span>
              )}
              {isUploading ? null : mode === 'record' && recorder.recording ? (
                <>
                  <output className="mb-2 block font-medium text-base text-system-white">
                    {t('speech.recording')}
                  </output>
                  <p className="mb-5 font-mono text-2xl text-system-white tabular-nums">
                    {formatRecordingTime(recorder.elapsed)}
                  </p>
                  <button
                    type="button"
                    onClick={recorder.stop}
                    className="flex min-h-11 items-center gap-2 rounded-full bg-system-white px-5 font-medium text-sm text-system-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-4"
                  >
                    <Square className="size-4" aria-hidden="true" />
                    {t('speech.recordStop')}
                  </button>
                </>
              ) : source && !isUploading ? (
                <div className="w-full max-w-[420px]">
                  <StudioAudioPlayer
                    url={source}
                    id={currentAudio?.id ?? source}
                    duration={duration}
                    label={t(mode === 'record' ? 'speech.recordName' : 'speech.audio')}
                  />
                  <div className="mt-5 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={mode === 'audio' ? selectAudio : recorder.start}
                      disabled={recorder.starting}
                      className="flex min-h-11 items-center gap-2 rounded-full border border-system-white16 px-4 text-sm text-system-white transition-colors hover:bg-system-white08 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 motion-reduce:transition-none"
                    >
                      <RefreshCw className="size-4" aria-hidden="true" />
                      {t(mode === 'audio' ? 'speech.audioReplace' : 'speech.recordRetry')}
                    </button>
                    <button
                      type="button"
                      onClick={mode === 'audio' ? onRemoveAudio : onRemoveRecording}
                      aria-label={t('speech.audioRemove')}
                      className="flex size-11 items-center justify-center rounded-full border border-system-white16 text-system-white64 transition-colors hover:bg-system-white08 hover:text-system-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 motion-reduce:transition-none"
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mb-5 max-w-[360px] text-sm text-system-white64 leading-6">
                    {t(mode === 'audio' ? 'speech.audioHint' : 'speech.recordLimit')}
                  </p>
                  {mode === 'audio' ? (
                    <span className="flex min-h-11 items-center rounded-full bg-system-white px-6 font-medium text-sm text-system-black">
                      {t('speech.audio')}
                    </span>
                  ) : (
                    <span className="flex min-h-11 items-center rounded-full bg-system-white px-6 font-medium text-sm text-system-black">
                      {t('speech.recordStart')}
                    </span>
                  )}
                </>
              )}
              {!isUploading &&
              mode === 'record' &&
              (!recorder.supported || (recorder.error && recorder.error !== 'too-short')) ? (
                <p role="alert" className="mt-4 max-w-[420px] text-sm text-system-white64">
                  {t(recorder.error === 'denied' ? 'speech.recordDenied' : 'speech.recordUnsupported')}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
      {/* 三种模式统一：按钮固定占据底部空间，中间内容按剩余高度收缩 */}
      <div
        data-speech-footer
        className="mt-4 flex shrink-0 justify-end bg-system-bg01 sm:pb-[env(safe-area-inset-bottom)]"
      >
        {nextButton}
      </div>
    </section>
  );
}

function formatRecordingTime(seconds: number) {
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}
